import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import shopWebsiteService from '@/services/shopWebsiteService'

const DEFAULT_PLATFORM_HOSTS = ['localhost', '127.0.0.1']

function platformHosts(): string[] {
  const raw = import.meta.env.VITE_PLATFORM_HOSTS as string | undefined
  const fromEnv = raw
    ? raw.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean)
    : []
  return [...new Set([...DEFAULT_PLATFORM_HOSTS, ...fromEnv])]
}

/**
 * Exact hostname match only (plus optional www twin of each listed host).
 * Do NOT treat `*.storesch.com` as platform when `storesch.com` is listed —
 * env storefronts like `dev.storesch.com` / `uat.storesch.com` must stay tenant hosts.
 */
function isPlatformHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/:\d+$/, '')
  const expanded = new Set<string>()
  for (const h of platformHosts()) {
    const cleaned = h.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/:\d+$/, '')
    if (!cleaned) continue
    expanded.add(cleaned)
    if (cleaned.startsWith('www.')) expanded.add(cleaned.slice(4))
    else expanded.add(`www.${cleaned}`)
  }
  return expanded.has(host)
}

function isAppInternalPath(pathname: string): boolean {
  return (
    pathname.startsWith('/shop/') ||
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/auth') ||
    pathname.startsWith('/customer') ||
    pathname.startsWith('/api') ||
    pathname.startsWith('/forms/')
  )
}

export type CustomDomainShop = {
  slug: string
  storefront_access?: string
}

function softRedirectUrl(
  baseUrl: string,
  entryPath: string,
  pathname: string,
  search: string,
  hash: string,
): string {
  const base = String(baseUrl || '').replace(/\/$/, '')
  const entry = String(entryPath || '/').trim() || '/'
  const pathOnly = pathname || '/'
  const targetPath =
    pathOnly === '/' || pathOnly === '' ? (entry === '/' ? '' : entry) : pathOnly
  return `${base}${targetPath}${search || ''}${hash || ''}`
}

function readInitialState(): 'loading' | CustomDomainShop | null {
  if (typeof window === 'undefined') return 'loading'
  if (isPlatformHost(window.location.hostname) || isAppInternalPath(window.location.pathname)) {
    return null
  }
  return 'loading'
}

export function useCustomDomainShop(): 'loading' | CustomDomainShop | null {
  const location = useLocation()
  const [state, setState] = useState<'loading' | CustomDomainShop | null>(readInitialState)

  useEffect(() => {
    const host = window.location.hostname
    const path = location.pathname
    if (isPlatformHost(host) || isAppInternalPath(path)) {
      setState(null)
      return
    }
    let cancelled = false
    shopWebsiteService
      .getByHost()
      .then((res) => {
        if (cancelled) return
        const soft = res.data?.soft_redirect
        if (soft?.base_url) {
          const target = softRedirectUrl(
            soft.base_url,
            soft.entry_path || '/',
            location.pathname,
            location.search,
            location.hash,
          )
          if (target && target !== window.location.href) {
            window.location.replace(target)
            return
          }
        }
        if (res.data?.slug) {
          setState({
            slug: res.data.slug,
            storefront_access: res.data.storefront_access,
          })
        } else {
          setState(null)
        }
      })
      .catch(() => {
        if (!cancelled) setState(null)
      })
    return () => {
      cancelled = true
    }
  }, [location.pathname, location.search, location.hash])

  return state
}
